﻿<template>
    <!--Topbar-->
    <div class="flex flex-row h-3rem pb-1">
        <div class="flex flow-row justify-content-start">
            <img :src="util$.assets$.brand.logo" alt="Logo" />
        </div>
        <div class="flex flex-row w-screen justify-content-end">
            <!-- 閒置倒數（已登入且後端有設定 idleTimeout 才顯示）：剩餘時間歸零自動登出 -->
            <div v-if="util$.pinia$.security.userInfo.jti && idleEnabled"
                 class="flex pr-3 align-items-center text-sm text-color-secondary"
                 v-tooltip.bottom="$t('topBar.idleHint')">
                <i class="fa-solid fa-clock pr-1" />
                <span>{{ idleText }}</span>
            </div>
            <div class="flex pr-3">
                <Button severity="danger" icon="fa-solid fa-arrows-rotate" @click="onReload" v-tooltip.bottom="$t('topBar.reload')"></Button>
            </div>
            <div class="flex pr-3">
                <Button severity="help" icon="fa-solid fa-palette" @click="visibleThemeStyle=true" v-tooltip.bottom="$t('topBar.theme')"></Button>
            </div>
            <div class="flex pr-3">
                <Button severity="secondary" icon="fa-solid fa-globe" @click="toggleLocale" v-tooltip.bottom="$t('topBar.language')"></Button>
            </div>
            <div v-if="util$.pinia$.security.userInfo.jti" class="flex flex-row">
                <div class="flex pr-3 align-items-center cursor-pointer" v-tooltip.bottom="$t('topBar.accMgmt')">
                    <Chip :label="util$.pinia$.security.userInfo.name" @click="visibleAccMgmt=true"></Chip>
                </div>
                <div class="flex pr-3">
                    <Button icon="fa-solid fa-right-from-bracket" severity="danger" @click="confirmLogout" v-tooltip.bottom="$t('topBar.logout')" />
                </div>
            </div>
        </div>
    </div>
    <ThemeStyle v-model:visible="visibleThemeStyle"></ThemeStyle>
    <AccMgmt v-model:visible="visibleAccMgmt" v-if="util$.pinia$.security.userInfo.jti"></AccMgmt>
</template>

<style scoped>
</style>

<script lang="ts">
    //normal <script> executed in module scope (only once)
    //只會執行一次(第1次) 除非按下F5

    export default {
        name: module$.topBar,
        inheritAttrs: false
    }
</script>

<script setup lang="ts">
    import { ref, computed, watch, onUnmounted } from 'vue'
    import { utilfcty$ } from '../utils/utility'
    import { module$ } from '../enums'
    import ThemeStyle from '../components/ThemeStyle.vue'
    import { storeToRefs } from 'pinia'
    import { useI18n } from 'vue-i18n'

    //***************工具物件***************
    const util$ = utilfcty$.vueUse();
    //locales/{locales}/topBar.json
    const { t, locale } = useI18n(); // t: 翻譯函式; locale: 當前語系(響應式)
    const store = util$.pinia$.app;
    const { visibleThemeStyle, visibleAccMgmt } = storeToRefs(store);

    //***************屬性/計算**************
    // 閒置自動登出設定：由後端 Bootstrap 取（parameters.idleLogout = { enabled, timeoutMinutes }），
    // 對應 appsettings MPPNext:Security:IdleLogout。enabled 為 false、timeoutMinutes <= 0、或後端未提供（舊版）則不啟用。
    // 用途：防使用者離開座位後 session 遭他人使用——與 JWT 有效期無關，純前端閒置計時。
    const idleCfg = util$.pinia$.app.parameters?.idleLogout as { enabled: boolean; timeoutMinutes: number } | undefined;
    const idleEnabled = !!idleCfg?.enabled && idleCfg.timeoutMinutes > 0;
    const idleTotalSec = idleEnabled ? idleCfg!.timeoutMinutes * 60 : 0;

    // 剩餘秒數（顯示用，每秒遞減；操作則重置回 idleTotalSec）
    const remaining = ref(idleTotalSec);

    // 倒數顯示（mm:ss）
    const idleText = computed(() => {
        const s = Math.max(remaining.value, 0);
        return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
    });

    //***************計時 / 操作監聽**************
    let countdownTimer: any = null;   // 每秒遞減 + 歸零檢查
    let lastActivity = 0;             // 上次「實際重置」時間（throttle 用）
    const ACTIVITY_THROTTLE_MS = 1000;   // 操作重置節流：1 秒內最多重置一次（mousemove 高頻，避免每次都重置）

    // 監聽的操作事件：滑鼠移動 / 鍵盤 / 點擊 / 捲動 / 觸控（看 stats 不按鈕也算操作）
    const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'] as const;

    // 操作 → 重置倒數（throttle：1 秒內多次操作只重置一次，降低開銷）
    const onActivity = () => {
        const now = Date.now();
        if (now - lastActivity < ACTIVITY_THROTTLE_MS) return;
        lastActivity = now;
        remaining.value = idleTotalSec;   // 重置回滿
    };

    // 每秒倒數；歸零 → 閒置自動登出
    const startCountdown = () => {
        stopCountdown();
        remaining.value = idleTotalSec;
        countdownTimer = setInterval(() => {
            remaining.value -= 1;
            if (remaining.value <= 0) {
                idleLogout();
            }
        }, 1000);
    };
    const stopCountdown = () => {
        if (countdownTimer) { clearInterval(countdownTimer); countdownTimer = null }
    };

    // 閒置自動登出（比照 JWT 過期：清 session + 導 login 帶 redirect 回原頁；屬被動失效，與主動登出不同——主動登出不記原頁）。
    const idleLogout = () => {
        stopCountdown();
        removeActivityListeners();
        // sessionExpired：被動失效共用流程（reset + 導 login 帶當前頁 redirect）。
        util$.pinia$.security.sessionExpired(
            `<strong>${t('common.sessionExpired.idle.summary')}</strong><br/>${t('common.sessionExpired.idle.detail')}`
        );
    }

    const addActivityListeners = () => {
        ACTIVITY_EVENTS.forEach(ev => document.addEventListener(ev, onActivity, { passive: true }));
    };
    const removeActivityListeners = () => {
        ACTIVITY_EVENTS.forEach(ev => document.removeEventListener(ev, onActivity));
    };

    //***************元件函式**************
    /** 切換語系:zh-TW ↔ en-US。locale 為響應式,改值即觸發全站(含標題/PrimeVue)更新。 */
    const toggleLocale = () => {
        locale.value = locale.value === 'zh-TW' ? 'en-US' : 'zh-TW';
    }

    const confirmLogout = (e: Event) => {
        util$.confirm$?.open(e, t('topBar.logoutHeader'), t('topBar.logoutQuestion'), async () => {
            await util$.pinia$.security.logout();
        }, () => { }, 'dialog');
    }

    const onReload = () => {
        util$.pinia$.app.refreshPage();
    }

    //***************元件監看**************
    // 監聽 jti（登入狀態）啟停閒置倒數，而非在 onMounted 一次性判斷：
    // 登入流程中 jti 是「登入成功後才 $patch 設定」，可能晚於 TopBar 建立／onMounted（導致 onMounted 當下讀到空 jti 而未啟動，需 F5 才正常）。
    // 改用 watch + immediate：F5 時 jti 已從 cookie 還原 → immediate 即啟動；登入時 jti 由空變有值 → watch 觸發啟動；登出 jti 清空 → 停止。
    watch(
        () => util$.pinia$.security.userInfo.jti,
        (jti) => {
            if (idleEnabled && jti) {
                addActivityListeners();
                startCountdown();
            } else {
                stopCountdown();
                removeActivityListeners();
            }
        },
        { immediate: true }
    );

    //***************元件鉤子**************
    onUnmounted(() => {
        stopCountdown();
        removeActivityListeners();
    });

</script>